import { visualDesignNormalizer } from '@eventdex/builder/src/store/normalizers';
import type { RealtimeSource } from '@components/Gala/seatAssignment';

/**
 * Keeps the room layout on the public seating view current when the Design is
 * saved without any Seat Assignment changing.
 *
 * Every save of a Design is broadcast as `design-updated` with the whole
 * serialized Design (`pk`, `template_json`, …). The builder module folds that
 * into its store as-is, so the store keeps the stale `template` beside the new
 * `template_json`; the payload is normalized here instead and handed over whole.
 */
export interface LiveDesignOptions {
  designId: string | number;
  /** Receives the saved Design, in the shape `useVisualDesigns` lists. */
  onDesign: (design: any) => void;
  /** Where the realtime events arrive. Defaults to `window`. */
  source?: RealtimeSource;
}

const UPDATE_METHODS = ['put', 'patch'];

export function attachLiveDesign(options: LiveDesignOptions): () => void {
  const { designId, onDesign } = options;
  const source =
    options.source ?? (typeof window !== 'undefined' ? (window as unknown as RealtimeSource) : undefined);

  const onDesignUpdated = (event: Event) => {
    const design = (event as CustomEvent).detail as Record<string, any> | undefined;
    if (!design || String(design.pk) !== String(designId)) return;
    if (!UPDATE_METHODS.includes(String(design.method).toLowerCase())) return;
    if (!design.template_json?.pageData?.[0]?.templateData) return;
    const { method, ...saved } = design;
    onDesign(visualDesignNormalizer(saved));
  };

  source?.addEventListener('designUpdated', onDesignUpdated);
  return () => source?.removeEventListener('designUpdated', onDesignUpdated);
}
